import React, { ReactNode } from 'react'
import { FaArrowLeft } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { cx } from '../cx'
import s from './styles.module.scss'

export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Ignore the 1280px max width (full-bleed tables). */
    fluid?: boolean
    /** Tighter vertical rhythm between sections. */
    gap?: 'md' | 'sm'
    children: ReactNode
}

/** Content column for every view: max width, centred, consistent section gap. */
export const Page = ({ fluid = false, gap = 'md', className, children, ...rest }: PageProps) => (
    <div className={cx(s.page, fluid && s.fluid, gap === 'sm' && s.gapSm, className)} {...rest}>
        {children}
    </div>
)

export interface PageHeaderProps {
    title: ReactNode
    /** Small uppercase label above the title (section / area name). */
    eyebrow?: ReactNode
    /** One or two sentences explaining what the page is for. */
    intro?: ReactNode
    /** Inline facts: counts, last refreshed, environment. */
    meta?: ReactNode
    /** Buttons aligned right. */
    actions?: ReactNode
    /** Router path or callback rendered as a "Back" link above the title. */
    backTo?: string | (() => void)
    backLabel?: string
    /** Smaller title for nested/detail headers. */
    compact?: boolean
    className?: string
}

/** Standard page header: back link, eyebrow, title, intro, meta and actions. */
export const PageHeader = ({
    title,
    eyebrow,
    intro,
    meta,
    actions,
    backTo,
    backLabel = 'Back',
    compact = false,
    className,
}: PageHeaderProps) => {
    const back =
        typeof backTo === 'string' ? (
            <Link to={backTo} className={s.back}>
                <FaArrowLeft aria-hidden="true" /> {backLabel}
            </Link>
        ) : typeof backTo === 'function' ? (
            <button type="button" className={s.back} onClick={backTo}>
                <FaArrowLeft aria-hidden="true" /> {backLabel}
            </button>
        ) : null

    return (
        <header className={cx(s.header, compact && s.compact, className)}>
            {back}
            <div className={s.headerRow}>
                <div className={s.headerText}>
                    {eyebrow && <span className={s.eyebrow}>{eyebrow}</span>}
                    <h1 className={s.title}>{title}</h1>
                    {intro && <p className={s.intro}>{intro}</p>}
                    {meta && <div className={s.meta}>{meta}</div>}
                </div>
                {actions && <div className={s.actions}>{actions}</div>}
            </div>
        </header>
    )
}

export interface SectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
    title?: ReactNode
    description?: ReactNode
    actions?: ReactNode
    /** Optional: a header-only Section is a valid way to title the content that follows it. */
    children?: ReactNode
}

/** Titled block within a page. Use for grouping cards/tables under a heading. */
export const Section = ({ title, description, actions, className, children, ...rest }: SectionProps) => (
    <section className={cx(s.section, className)} {...rest}>
        {(title || actions) && (
            <div className={s.sectionHead}>
                <div>
                    {title && <h2 className={s.sectionTitle}>{title}</h2>}
                    {description && <p className={s.sectionDesc}>{description}</p>}
                </div>
                {actions && <div className={s.actions}>{actions}</div>}
            </div>
        )}
        {children}
    </section>
)
